
import React, { useEffect, useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { AgeGroupVariationFormData } from "../schemas/ageGroupVariationSchema";
import { GradeOption } from '../hooks/useAgeGroupVariationForm';
import { RequiredFieldLabel } from '@/components/ui/required-field-label';

interface MaxAgeSectionProps {
  form: UseFormReturn<AgeGroupVariationFormData>;
  gradeOptions: GradeOption[];
}

export const MaxAgeSection: React.FC<MaxAgeSectionProps> = ({
  form,
  gradeOptions
}) => {
  const showAgeFirst = !form.watch('showMaxAgeGradeAsPrimary'); 
  
  // Use state to preserve values during position swapping
  const [maxAgeValue, setMaxAgeValue] = useState<number | null>(form.getValues().maxAgeInYears);
  const [maxGradeValue, setMaxGradeValue] = useState<string>(form.getValues().maxAgeGradeKey || "none");
  const [maxAgeNote, setMaxAgeNote] = useState<string>(form.getValues().maxAgeNote || "");
  
  // Update local state when form values change
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.maxAgeInYears !== undefined) {
        setMaxAgeValue(value.maxAgeInYears);
      }
      if (value.maxAgeGradeKey !== undefined) {
        setMaxGradeValue(value.maxAgeGradeKey || "none");
      }
      if (value.maxAgeNote !== undefined) {
        setMaxAgeNote(value.maxAgeNote || "");
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);
  
  // Force update form values whenever local state or position changes
  useEffect(() => {
    // Only update the form if the values actually exist
    if (maxAgeValue !== undefined) {
      form.setValue('maxAgeInYears', maxAgeValue, { shouldDirty: true, shouldValidate: true });
    }
    
    if (maxGradeValue !== undefined) {
      const gradeKey = maxGradeValue === "none" ? "" : maxGradeValue;
      form.setValue('maxAgeGradeKey', gradeKey, { shouldDirty: true, shouldValidate: true });
    }
    
    if (maxAgeNote !== undefined) {
      form.setValue('maxAgeNote', maxAgeNote, { shouldDirty: true, shouldValidate: true });
    }
  }, [showAgeFirst, maxAgeValue, maxGradeValue, maxAgeNote, form]);
  
  // Create controlled components to preserve values regardless of order
  const renderMaxAgeField = () => (
    <FormField
      control={form.control}
      name="maxAgeInYears"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">
            {showAgeFirst 
              ? <RequiredFieldLabel label="Max age, in years" />
              : "Max age, in years (optional if using grade)"}
          </FormLabel>
          <FormControl>
            <Input
              type="number"
              min={0}
              max={20}
              step="0.1" // Allow decimal values like 1.5
              value={maxAgeValue !== null ? maxAgeValue : ''}
              onChange={e => {
                const newValue = e.target.value ? Number(e.target.value) : null;
                setMaxAgeValue(newValue);
                field.onChange(newValue);
              }}
              placeholder="Enter age"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderMaxGradeField = () => (
    <FormField
      control={form.control}
      name="maxAgeGradeKey"
      rules={{
        validate: (value) => {
          // If Age is primary, always allow any value
          if (showAgeFirst) return true;
          
          // If Grade is primary, require a non-empty value that isn't "none"
          return value !== undefined && value !== "" && value !== null;
        }
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-left block">
            {showAgeFirst 
              ? "Max grade (optional if using years old)" 
              : <RequiredFieldLabel label="Max grade" />}
          </FormLabel>
          <Select
            onValueChange={value => {
              setMaxGradeValue(value);
              field.onChange(value === "none" ? "" : value);
            }}
            value={maxGradeValue || "none"}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {gradeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <div className="space-y-4 pr-1">
      <div className="flex items-center gap-2 mb-4">
        <FormLabel htmlFor="showMaxAgeGradeAsPrimary" className="text-left block whitespace-nowrap">
          Show max age based on:
        </FormLabel>
        <FormField
          control={form.control}
          name="showMaxAgeGradeAsPrimary"
          render={({ field }) => (
            <Select
              onValueChange={value => {
                field.onChange(value === "true");
              }}
              value={field.value ? "true" : "false"}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">Years old</SelectItem>
                <SelectItem value="true">Grade</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showAgeFirst ? (
        <>
          {renderMaxAgeField()}
          {renderMaxGradeField()}
        </>
      ) : (
        <>
          {renderMaxGradeField()}
          {renderMaxAgeField()}
        </>
      )}

      <FormField
        control={form.control}
        name="maxAgeNote"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-left block">Max age footnote (optional)</FormLabel>
            <FormControl>
              <Input 
                value={maxAgeNote}
                onChange={e => {
                  setMaxAgeNote(e.target.value);
                  field.onChange(e.target.value);
                }}
                placeholder=""
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
